import React, { useCallback, useMemo } from 'react'
import { J1DownarrowD } from '@e3dc-react/icons'
import { Accordion, AccordionDetails, AccordionSummary, Box, Checkbox, MenuItem, Stack, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { AccordionFilterItem } from '../../models/charging-sessions.model'

const useStyles = makeStyles()(theme => ({
  accordion: {
    boxShadow: 'none',
    backgroundColor: theme.palette.shades.shade04,
    padding: 0,
    margin: 0,
    borderRadius: `${theme.spacing(2)} !important`,
    overflow: 'hidden',
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      margin: 0,
    },
  },
  accordionSummary: {
    display: 'flex',
    flexDirection: 'column',
  },
  filterItem: {
    padding: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    height: theme.shape.filter.menuItemHeight,
    minWidth: 0,
  },
  checkbox: {
    height: '30px',
    width: '30px',
    color: theme.palette.grey.grey03,
    '&.Mui-checked': {
      color: theme.palette.primary.main,
    },
  },
  filterOptionLabel: {
    minWidth: 0,
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
  },
}))

type FilterOption<T> = {
  label: string
  value: T
}

interface ChargingSessionMobileFilterItemProps<T> {
  filterOptions: FilterOption<T>[]
  selectedFilterOptions: T[]
  filterOptionLabel: string
  accordionFilterItemValue: AccordionFilterItem
  selectedAccordionFilterItem: AccordionFilterItem
  setAccordionFilterItem: (item: AccordionFilterItem) => void
  setSelectedFilters: (value: T[]) => void
}

/**
 * Component which renders one filter item for mobile filtering of charging sessions.
 * It displays sort item title and all possible options
 */
export const ChargingSessionMobileFilterItem = <T,>({
  filterOptions,
  selectedFilterOptions,
  filterOptionLabel,
  accordionFilterItemValue,
  setAccordionFilterItem,
  selectedAccordionFilterItem,
  setSelectedFilters,
}: ChargingSessionMobileFilterItemProps<T>): React.ReactElement => {
  const { classes } = useStyles()

  const handleFilterOptionChange = useCallback(
    (filterItem: FilterOption<T>) => {
      const newFilterValues = selectedFilterOptions.includes(filterItem.value)
        ? selectedFilterOptions.filter(i => i !== filterItem.value)
        : [...selectedFilterOptions, filterItem.value]

      setSelectedFilters(newFilterValues)
    },
    [setSelectedFilters, selectedFilterOptions]
  )

  const handleExpandAccordion = (): void => {
    setAccordionFilterItem(accordionFilterItemValue === selectedAccordionFilterItem ? false : accordionFilterItemValue)
  }

  const selectedFilterItemsWithLabels = useMemo(
    () => filterOptions.filter(filterItem => selectedFilterOptions.some(item => item === filterItem.value)),
    [filterOptions, selectedFilterOptions]
  )

  const shouldExpandAccordion = selectedAccordionFilterItem === accordionFilterItemValue

  return (
    <Box sx={{ borderRadius: 2, marginBottom: 2 }}>
      <Accordion
        expanded={shouldExpandAccordion}
        square={false}
        disableGutters
        elevation={0}
        className={classes.accordion}
        sx={theme => ({
          border: selectedFilterOptions.length > 0 ? `1px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.shades.shade03}`,
        })}
        onChange={handleExpandAccordion}
      >
        <AccordionSummary expandIcon={<J1DownarrowD id="charging-session-filter-accordion-arrow" sx={{ color: '#4A4A4A' }} />}>
          <Stack className={classes.accordionSummary}>
            <Typography variant="h3">{filterOptionLabel}</Typography>

            {!shouldExpandAccordion &&
              selectedFilterItemsWithLabels.map((option, index) => (
                <Typography variant="body2" key={`${option.label}-${option.value}`}>
                  {option.label}
                  {index < selectedFilterItemsWithLabels.length - 1 && ','}
                </Typography>
              ))}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          {filterOptions.map(item => {
            const isItemSelected = selectedFilterOptions.includes(item.value)
            return (
              <MenuItem key={`${item.value}-${item.label}`} className={classes.filterItem} onClick={() => handleFilterOptionChange(item)}>
                <Checkbox size="small" checked={isItemSelected} className={classes.checkbox} />
                <Typography variant="body2" className={classes.filterOptionLabel}>
                  {item.label}
                </Typography>
              </MenuItem>
            )
          })}
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}
